// ReopenActivityDrawer — single-screen audit context for a journal.
//
// Closes the "data exists but is scattered across 4 places" gap the
// CFO/auditor experiences today. Renders five sections in order:
//
//   1. Reopen Origin   — was this journal born in a reopen window?
//   2. Amendment Timeline — every journal_versions row with diffs.
//   3. Field Changes   — pretty before/after for the latest amendment.
//   4. Audit Events    — unified Facade events for this journal.
//   5. Quick Links     — to Differential Report, Versions, Audit Trail.
//
// Backed entirely by GET /api/journals/{id}/reopen-activity — a single
// round-trip. No writes from this component.
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { X, RefreshCw, History, FileDiff, AlertCircle, ExternalLink, ScrollText, GitCommit, Play } from 'lucide-react';

function formatDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch (_) {
    return s;
  }
}

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;
}

function stringifyValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 200 ? `${v.slice(0, 200)}…` : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 200 ? `${s.slice(0, 200)}…` : s;
  } catch (_) {
    return '—';
  }
}

function StatusPill({ status }) {
  const tone =
    status === 'posted'     ? 'bg-emerald-100 text-emerald-700' :
    status === 'superseded' ? 'bg-amber-100 text-amber-700' :
    status === 'draft'      ? 'bg-slate-100 text-slate-700' :
    'bg-slate-100 text-slate-600';
  return (
    <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${tone}`}>
      {status || '—'}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, testId }) {
  return (
    <div className="flex items-start gap-2 mb-3" data-testid={testId}>
      <div className="mt-0.5 text-purple-600"><Icon className="w-4 h-4" /></div>
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

export default function ReopenActivityDrawer({ journalId, onClose }) {
  const [data, setData] = useState(null);
  const [replay, setReplay] = useState(null);
  const [tab, setTab] = useState('activity');  // 'activity' | 'replay'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivity = async () => {
    if (!journalId) return;
    setLoading(true);
    setError(null);
    try {
      const [actRes, replayRes] = await Promise.all([
        axiosInstance.get(`/journals/${journalId}/reopen-activity`),
        axiosInstance.get(`/journals/${journalId}/audit-replay`).catch(() => ({ data: null })),
      ]);
      setData(actRes.data);
      setReplay(replayRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchActivity(); }, [journalId]);

  if (!journalId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="reopen-activity-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />
      <aside
        className="relative w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-purple-600" />
              <h2 className="text-base font-semibold text-slate-900" data-testid="drawer-title">
                Reopen Activity
              </h2>
              <span className="text-xs font-mono text-slate-500" data-testid="drawer-journal-no">
                {data?.journal?.journal_no || journalId.slice(0, 8)}
              </span>
            </div>
            {data?.journal?.description && (
              <p className="text-xs text-slate-500 mt-1 max-w-md truncate">
                {data.journal.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchActivity}
              data-testid="drawer-refresh-btn"
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              data-testid="drawer-close-btn"
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-6 bg-slate-50/40">
          <div className="flex gap-1 text-xs">
            <button
              onClick={() => setTab('activity')}
              className={`px-3 py-2 border-b-2 -mb-px ${tab === 'activity' ? 'border-purple-600 text-purple-700 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="drawer-tab-activity"
            >
              <span className="inline-flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" /> Activity
              </span>
            </button>
            <button
              onClick={() => setTab('replay')}
              className={`px-3 py-2 border-b-2 -mb-px ${tab === 'replay' ? 'border-purple-600 text-purple-700 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid="drawer-tab-replay"
            >
              <span className="inline-flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5" /> Replay
                {replay && replay.summary?.amendments_count > 0 && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                    {replay.summary.amendments_count}
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 text-sm">
          {loading && (
            <p className="text-slate-500 text-xs" data-testid="drawer-loading">Loading…</p>
          )}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 flex items-start gap-2"
                 data-testid="drawer-error">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
              <div className="text-xs text-red-700">{error}</div>
            </div>
          )}
          {!loading && !error && data && tab === 'replay' && (
            <ReplayPanel replay={replay} />
          )}
          {!loading && !error && data && tab === 'activity' && (
            <>
              {/* 1. Origin */}
              <section data-testid="drawer-section-origin">
                <SectionHeader
                  icon={History}
                  title="Reopen Origin"
                  subtitle="هل وُلد هذا القيد داخل نافذة Reopen؟"
                  testId="drawer-section-origin-header"
                />
                {data.reopen_tags?.created_during_reopen && data.origin_reopen_request ? (
                  <div className="rounded-md border border-purple-200 bg-purple-50/60 p-3 grid grid-cols-2 gap-y-1 gap-x-4 text-xs">
                    <div className="text-slate-500">Request</div>
                    <div className="font-mono text-purple-700" data-testid="drawer-origin-request-id">
                      {data.origin_reopen_request.id?.slice(0, 12)}…
                    </div>
                    <div className="text-slate-500">Period</div>
                    <div>{data.origin_reopen_request.period_name || '—'}</div>
                    <div className="text-slate-500">Status</div>
                    <div className="capitalize">{data.origin_reopen_request.status}</div>
                    <div className="text-slate-500">Approved By</div>
                    <div>{data.origin_reopen_request.approved_by_name || data.origin_reopen_request.approved_by || '—'}</div>
                    <div className="text-slate-500">Approved At</div>
                    <div>{formatDate(data.origin_reopen_request.approved_at)}</div>
                    {data.origin_reopen_request.reason && (
                      <>
                        <div className="text-slate-500">Reason</div>
                        <div className="text-slate-700">{data.origin_reopen_request.reason}</div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500" data-testid="drawer-origin-none">
                    لم يُنشأ داخل نافذة Reopen.
                  </p>
                )}
              </section>

              {/* 2. Amendment Timeline */}
              <section data-testid="drawer-section-amendments">
                <SectionHeader
                  icon={History}
                  title="Amendment Timeline"
                  subtitle={`${data.amendments?.length || 0} version(s) recorded`}
                  testId="drawer-section-amendments-header"
                />
                {(data.amendments || []).length === 0 ? (
                  <p className="text-xs text-slate-500" data-testid="drawer-amendments-empty">
                    لا توجد تعديلات على هذا القيد.
                  </p>
                ) : (
                  <ol className="relative border-s border-slate-200 ms-3 space-y-3">
                    {data.amendments.map((a) => (
                      <li key={a.id || a.version_number} className="ms-4"
                          data-testid={`drawer-amendment-${a.version_number}`}>
                        <span className="absolute -start-1.5 mt-1.5 h-3 w-3 rounded-full bg-purple-500 border border-white" />
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-slate-800">
                            Version {a.version_number}
                          </span>
                          <StatusPill status={a.status} />
                        </div>
                        <p className="text-[11px] text-slate-500">
                          {formatDate(a.posted_at || a.created_at)} · {a.created_by || '—'}
                        </p>
                        {a.reason && (
                          <p className="text-xs text-slate-700 mt-1 italic">&ldquo;{a.reason}&rdquo;</p>
                        )}
                        {(a.field_changes || []).length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {a.field_changes.map((c) => (
                              <li key={c.key} className="text-[11px] bg-slate-50 rounded px-2 py-1">
                                <span className="font-semibold text-slate-700">{c.key}:</span>{' '}
                                <span className="text-rose-700">{stringifyValue(c.before)}</span>{' '}
                                <span className="text-slate-400">→</span>{' '}
                                <span className="text-emerald-700">{stringifyValue(c.after)}</span>
                                {c.summary && (
                                  <span className="ml-2 text-slate-500">
                                    (Δ debit {fmtMoney(c.summary.total_debit_delta)}, lines {c.summary.line_count_delta >= 0 ? '+' : ''}{c.summary.line_count_delta})
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </section>

              {/* 3. Reopen tags summary card */}
              {(data.reopen_tags?.created_during_reopen || data.reopen_tags?.last_amended_during_reopen) && (
                <section data-testid="drawer-section-tags">
                  <SectionHeader
                    icon={FileDiff}
                    title="Reopen Linkage"
                    subtitle="كل نوافذ Reopen التي مسّت هذا القيد"
                    testId="drawer-section-tags-header"
                  />
                  <div className="flex flex-wrap gap-2">
                    {data.reopen_tags.created_during_reopen && (
                      <span className="text-[11px] px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                        Created during reopen
                      </span>
                    )}
                    {data.reopen_tags.last_amended_during_reopen && (
                      <span className="text-[11px] px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                        Last amended during reopen
                      </span>
                    )}
                    {(data.window_ids || []).map((wid) => (
                      <span key={wid}
                            className="text-[11px] px-2 py-1 bg-slate-100 text-slate-700 rounded-full font-mono">
                        {wid.slice(0, 10)}…
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* 4. Audit Events */}
              <section data-testid="drawer-section-audit">
                <SectionHeader
                  icon={ScrollText}
                  title="Audit Events"
                  subtitle={`${data.audit_events?.length || 0} unified row(s) · audit_log + audit_logs`}
                  testId="drawer-section-audit-header"
                />
                {(data.audit_events || []).length === 0 ? (
                  <p className="text-xs text-slate-500" data-testid="drawer-audit-empty">
                    لا توجد أحداث تدقيقية على هذا القيد.
                  </p>
                ) : (
                  <div className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-y-auto">
                    {data.audit_events.slice(0, 20).map((ev) => (
                      <div key={ev.id || `${ev.ts}-${ev.action}`}
                           className="p-2 text-xs flex items-start gap-2"
                           data-testid={`drawer-audit-event-${ev.id || ev.ts}`}>
                        <div className="w-2 h-2 mt-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-800">{ev.action || '—'}</span>
                            <span className="text-[10px] uppercase text-slate-400">
                              {ev._source_collection}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500">
                            {formatDate(ev.ts)} · {ev.user_name || ev.user_id || '—'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* 5. Quick links */}
              <section data-testid="drawer-section-links">
                <SectionHeader
                  icon={ExternalLink}
                  title="Quick Links"
                  subtitle="انتقال مباشر إلى البيانات التفصيلية"
                  testId="drawer-section-links-header"
                />
                <div className="flex flex-wrap gap-2">
                  {(data.window_ids || []).map((wid) => (
                    <a
                      key={`diff-${wid}`}
                      href={`/period-reopen?focus=${wid}`}
                      className="text-xs px-3 py-1.5 rounded-md border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100"
                      data-testid={`drawer-link-diff-${wid}`}
                    >
                      Differential Report · {wid.slice(0, 8)}…
                    </a>
                  ))}
                  <a
                    href="/audit-trail"
                    className="text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    data-testid="drawer-link-audit-trail"
                  >
                    Audit Trail Browser
                  </a>
                  <a
                    href="/period-reopen"
                    className="text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    data-testid="drawer-link-period-reopen"
                  >
                    Period Reopen Center
                  </a>
                </div>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────
// ReplayPanel — "Git blame" timeline view (vertical event stream).
// ───────────────────────────────────────────────────────────────────────
function ReplayPanel({ replay }) {
  if (!replay) {
    return (
      <p className="text-xs text-slate-500" data-testid="drawer-replay-unavailable">
        Replay غير متاح لهذا القيد.
      </p>
    );
  }
  const events = replay.events || [];
  return (
    <div data-testid="drawer-section-replay">
      <SectionHeader
        icon={GitCommit}
        title="Audit Replay"
        subtitle={`${replay.summary?.amendments_count || 0} amendment(s) · ${replay.summary?.total_events || 0} total event(s)`}
        testId="drawer-section-replay-header"
      />
      {events.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="drawer-replay-empty">
          لا توجد أحداث للعرض.
        </p>
      ) : (
        <ol className="relative border-s-2 border-slate-200 ms-3 space-y-4">
          {events.map((ev) => {
            const dot =
              ev.type === 'created' ? 'bg-emerald-500' :
              ev.type === 'amended' ? 'bg-purple-500' :
              'bg-slate-400';
            const label =
              ev.type === 'created' ? 'Created' :
              ev.type === 'amended' ? `Amended (v${ev.version_number})` :
              'Current State';
            return (
              <li key={`${ev.sequence}-${ev.type}`} className="ms-4"
                  data-testid={`drawer-replay-event-${ev.sequence}`}>
                <span className={`absolute -start-[7px] mt-1.5 h-3 w-3 rounded-full border-2 border-white ${dot}`} />
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-semibold text-slate-800">{label}</span>
                  {ev.reopen_window && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded font-mono"
                          title={`Reopen window ${ev.reopen_window.id}`}>
                      via {ev.reopen_window.id.slice(0, 8)}…
                      {ev.reopen_window.period_name ? ` · ${ev.reopen_window.period_name}` : ''}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500">
                  {formatDate(ev.at)}
                  {ev.actor && (ev.actor.user_name || ev.actor.user_id) && (
                    <> · {ev.actor.user_name || ev.actor.user_id}</>
                  )}
                </p>
                {ev.reason && (
                  <p className="text-xs text-slate-700 mt-1 italic">&ldquo;{ev.reason}&rdquo;</p>
                )}
                {ev.type === 'amended' && (ev.field_changes || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {ev.field_changes.map((c) => (
                      <li key={c.key} className="text-[11px] bg-slate-50 rounded px-2 py-1">
                        <span className="font-semibold text-slate-700">{c.key}:</span>{' '}
                        <span className="text-rose-700">{stringifyValue(c.before)}</span>{' '}
                        <span className="text-slate-400">→</span>{' '}
                        <span className="text-emerald-700">{stringifyValue(c.after)}</span>
                        {c.summary && (
                          <span className="ml-2 text-slate-500">
                            (Δ debit {fmtMoney(c.summary.total_debit_delta)}, lines {c.summary.line_count_delta >= 0 ? '+' : ''}{c.summary.line_count_delta})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {ev.type === 'current' && ev.snapshot && (
                  <div className="mt-2 text-[11px] grid grid-cols-2 gap-x-3 gap-y-0.5 bg-slate-50 rounded p-2">
                    <span className="text-slate-500">Total Debit</span>
                    <span className="text-right font-mono">{fmtMoney(ev.snapshot.total_debit)}</span>
                    <span className="text-slate-500">Total Credit</span>
                    <span className="text-right font-mono">{fmtMoney(ev.snapshot.total_credit)}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
